/**
 * Quantity Selector Dropdown
 * Handles dropdown quantity selector with custom input option
 * Integrates with StateManager and enhanced add-to-cart system
 */

class QuantitySelector {
  constructor() {
    this.dropdown = document.querySelector('.quantity-dropdown');
    this.customInputWrapper = document.querySelector('.quantity-custom-input-wrapper');
    this.customInput = document.querySelector('.custom-quantity-field');
    this.hiddenQuantityInput = document.querySelector('input[name="quantity"]');

    if (!this.dropdown || !this.customInputWrapper || !this.customInput || !this.hiddenQuantityInput) {
      console.warn('[QuantitySelector] Required elements not found');
      return;
    }

    this.init();
  }

  init() {
    // Set up event listeners
    this.dropdown.addEventListener('change', this.handleDropdownChange.bind(this));
    this.customInput.addEventListener('input', this.handleCustomInputChange.bind(this));
    this.customInput.addEventListener('blur', this.validateCustomInput.bind(this));

    // Listen for variant changes to reset quantity
    if (typeof window.StateManager !== 'undefined') {
      window.StateManager.on('variant:changed', this.handleVariantChange.bind(this));
    }

    console.log('[QuantitySelector] Initialized');
  }

  handleDropdownChange(event) {
    const value = event.target.value;

    if (value === 'custom') {
      // Show custom input
      this.showCustomInput();
    } else {
      // Hide custom input and update quantity
      this.hideCustomInput();
      this.updateQuantity(parseInt(value, 10));
    }
  }

  handleCustomInputChange(event) {
    let value = parseInt(event.target.value, 10);

    // Validate minimum of 1
    if (value < 1) {
      value = 1;
      this.customInput.value = value;
    }

    // Validate maximum of 50,000
    if (value > 50000) {
      value = 50000;
      this.customInput.value = value;
    }

    // Update hidden quantity input
    this.updateQuantity(value);
  }

  validateCustomInput() {
    let value = parseInt(this.customInput.value, 10);

    // Ensure valid number (minimum 1, maximum 50,000)
    if (isNaN(value) || value < 1) {
      value = 1;
      this.customInput.value = value;
      this.updateQuantity(value);
    } else if (value > 50000) {
      value = 50000;
      this.customInput.value = value;
      this.updateQuantity(value);
    }
  }

  showCustomInput() {
    this.customInputWrapper.classList.add('show');
    this.customInputWrapper.style.display = 'block';
    this.customInput.focus();

    // Set initial value and update hidden input
    const currentValue = parseInt(this.customInput.value, 10);
    this.updateQuantity(currentValue >= 1 ? currentValue : 1);
  }

  hideCustomInput() {
    this.customInputWrapper.classList.remove('show');
    this.customInputWrapper.style.display = 'none';
  }

  updateQuantity(quantity) {
    // Update hidden input for form submission
    this.hiddenQuantityInput.value = quantity;

    // Dispatch custom event for other systems (StateManager, sticky button)
    if (typeof window.StateManager !== 'undefined') {
      window.StateManager.dispatchEvent(new CustomEvent('quantity:changed', {
        detail: { quantity: quantity }
      }));
    }

    // Also dispatch native change event on hidden input
    this.hiddenQuantityInput.dispatchEvent(new Event('change', { bubbles: true }));

    console.log(`[QuantitySelector] Quantity updated to: ${quantity}`);
  }

  handleVariantChange() {
    // Reset to default quantity (1) when variant changes
    this.dropdown.value = '1';
    this.hideCustomInput();
    this.updateQuantity(1);
    console.log('[QuantitySelector] Reset to default quantity on variant change');
  }

  /**
   * Public method to get current quantity
   * @returns {number} Current quantity value
   */
  getQuantity() {
    return parseInt(this.hiddenQuantityInput.value, 10) || 1;
  }

  /**
   * Public method to set quantity programmatically
   * @param {number} quantity - Quantity to set
   */
  setQuantity(quantity) {
    const qty = parseInt(quantity, 10);

    if (qty >= 1 && qty <= 10) {
      this.dropdown.value = qty.toString();
      this.hideCustomInput();
      this.updateQuantity(qty);
    } else if (qty > 10) {
      this.dropdown.value = 'custom';
      this.customInput.value = qty;
      this.showCustomInput();
    }
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.quantitySelector = new QuantitySelector();
  });
} else {
  window.quantitySelector = new QuantitySelector();
}
